import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';

/**
 * VerifyEmail Component
 * 
 * Handles email verification when user clicks the verification link from their email.
 * 
 * ROLE-BASED REDIRECT LOGIC (Jan 14, 2026):
 * After successful verification, redirects user to the appropriate login page:
 * - Customers → /customer/login/{calendar_symbolic_name} (calendar-scoped login per Rule #3)
 * - Calendar Admins → /login (admin login)
 * - Super Admins → /login (admin login)
 * 
 * The user's email is pre-filled on the login page for convenience.
 */
const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  
  // Store verified user info for role-based redirect
  // This data comes from the backend verify-email endpoint
  const [verifiedUser, setVerifiedUser] = useState(null);
  
  const token = searchParams.get('token');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Use a flag to prevent duplicate calls (React StrictMode, browser prefetch, etc.)
    let isSubscribed = true;
    
    const doVerify = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. No token provided.');
        return;
      }
      
      // Skip if already completed
      if (status !== 'verifying') return;
      
      try {
        const response = await fetch(`${API_URL}/api/auth/verify-email?token=${token}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();

        // Only update state if still subscribed (component not unmounted)
        if (!isSubscribed) return;

        if (response.ok && data.success) {
          setStatus('success');
          setMessage(data.message);
          
          // Store user info from backend response for role-based redirect
          setVerifiedUser({
            email: data.email,
            role: data.role,
            calendarSymbolicName: data.calendar_symbolic_name
          });
        } else {
          // Check if the error message indicates already verified
          const errorMsg = data.detail || data.message || 'Verification failed';
          
          // If token was already used, treat as success (likely duplicate request)
          if (errorMsg.includes('already been used') || errorMsg.includes('already verified') || errorMsg.includes('try logging in')) {
            setStatus('success');
            setMessage('Your email has been verified! You can now log in.');
          } else {
            setStatus('error');
            setMessage(errorMsg);
          }
        }
      } catch (err) {
        if (isSubscribed) {
          setStatus('error');
          setMessage('An error occurred during verification. Please try again.');
        }
      }
    };
    
    doVerify();
    
    return () => {
      isSubscribed = false;
    };
  }, [token, API_URL, status]);

  /**
   * Handle redirect to appropriate login page based on user role
   * 
   * CUSTOMER FLOW (Calendar-Scoped - Rule #3):
   * - Customers are tied to a specific calendar
   * - Must login via /customer/login/{symbolic_name} with calendar context
   * - Email is saved to localStorage for pre-fill
   * 
   * ADMIN FLOW:
   * - Calendar Admins and Super Admins use global /login page
   * - Email is saved to localStorage for pre-fill
   */
  const handleGoToLogin = () => {
    if (verifiedUser?.role === 'customer' && verifiedUser?.calendarSymbolicName) {
      // Customer: Save email and redirect to calendar-scoped customer login
      // The CustomerLogin component reads 'rememberedCustomerEmail' from localStorage
      localStorage.setItem('rememberedCustomerEmail', verifiedUser.email);
      navigate(`/customer/login/${verifiedUser.calendarSymbolicName}`);
    } else {
      // Calendar Admin or Super Admin: Save email and redirect to admin login
      // The Login component reads 'rememberedEmail' from localStorage
      localStorage.setItem('rememberedEmail', verifiedUser?.email || '');
      navigate('/login');
    }
  };

  /**
   * Dynamic button text based on user role
   * Customers see "Go to Customer Login", admins see "Go to Login"
   */
  const getButtonText = () => {
    if (verifiedUser?.role === 'customer') {
      return 'Go to Customer Login';
    }
    return 'Go to Login';
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <section className="flex-1 flex items-center justify-center bg-gradient-to-br from-white to-amber-50 py-20">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            {status === 'verifying' && (
              <>
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
                </div>
                <h1 className="text-2xl font-bold text-gray-800 mb-4">Verifying Your Email</h1>
                <p className="text-gray-600">Please wait while we verify your email address...</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h1 className="text-2xl font-bold text-gray-800 mb-4">Email Verified!</h1>
                <p className="text-gray-600 mb-2">{message}</p>
                {/* Show pre-fill notice to user */}
                {verifiedUser?.email && (
                  <p className="text-sm text-gray-500 mb-6">
                    Your email <span className="font-medium">{verifiedUser.email}</span> will be pre-filled on the login page.
                  </p>
                )}
                <button
                  onClick={handleGoToLogin}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                  data-testid="go-to-login-btn"
                >
                  {getButtonText()}
                </button>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
                <h1 className="text-2xl font-bold text-gray-800 mb-4">Verification Failed</h1>
                <p className="text-gray-600 mb-6">{message}</p>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/resend-verification')}
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                    data-testid="resend-btn"
                  >
                    Resend Verification Email
                  </button>
                  <button
                    onClick={() => navigate('/login')}
                    className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition-colors"
                  >
                    Back to Login
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default VerifyEmail;
